import { Button } from '@1trading/react/components/ui/button';
import { InfoIcon } from 'lucide-react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { getDifferingPersonDetails, type InstitutionPersonInRole } from '@/features/institution/schemas/person';

type InstitutionPersonDetailsDifferNoticeProps<Person extends InstitutionPersonInRole> = {
  // The copy's path in the form, watched here alone so typing re-renders this notice, not every person
  name: string;
  source: InstitutionPersonInRole | undefined;
  section: 'ubos' | 'seniorManagingOfficial' | 'ownership';
  onUseSource: (person: Person, source: InstitutionPersonInRole) => void;
};

// A copied person is prefilled from their entry, not linked to it, so a difference is pointed out rather than refused
export const InstitutionPersonDetailsDifferNotice = <Person extends InstitutionPersonInRole>({
  name,
  source,
  section,
  onUseSource,
}: InstitutionPersonDetailsDifferNoticeProps<Person>) => {
  const person: Person | undefined = useWatch({ name });
  const { t } = useTranslation();
  const differingDetails = person && source ? getDifferingPersonDetails(person, source) : [];
  const context = section === 'ownership' ? undefined : section;

  if (!person || !source || !differingDetails.length) return null;

  return (
    // Polite, as it comes and goes while the details are typed
    <Alert variant="info" role="status">
      <InfoIcon aria-hidden className="size-4" />
      <AlertDescription className="space-y-3">
        <p>
          {t('institutionDetails.personDetailsDiffer.notice', {
            context,
            name: `${source.firstName} ${source.lastName}`,
            details: differingDetails.map((detail) => t(`institutionDetails.person.${detail}`)).join(', '),
          })}
        </p>
        <Button size="sm" type="button" variant="outline" onClick={() => onUseSource(person, source)}>
          {t('institutionDetails.personDetailsDiffer.useSource', { context })}
        </Button>
      </AlertDescription>
    </Alert>
  );
};
InstitutionPersonDetailsDifferNotice.displayName = 'InstitutionPersonDetailsDifferNotice';
